import { Component, inject } from '@angular/core';

import { TranslateModule } from "@ngx-translate/core";
import { RxState, selectSlice } from "@rx-angular/state";
import { MasterarbeitsanmeldungVariables } from "../masterarbeitsanmeldung.variables";
import { RelatedContentRepresentation } from "@alfresco/js-api/typings/src/api/activiti-rest-api/model/relatedContentRepresentation";
import { AsyncPipe, DatePipe, NgForOf, NgIf } from "@angular/common";
import { WorkflowResultProtocol } from "@alf-nx-workspace/shared/interfaces";
import { BaseResultTemplateComponent } from "../../base-result-template.component";
import { WorkflowApprover } from "@alf-nx-workspace/flow/interfaces";
import { fromPromise } from "rxjs/internal-compatibility";
import { filter, map, switchMap, tap } from "rxjs/operators";
import { of, Subject } from "rxjs";
import { ActivitiContentService } from "@alfresco/adf-core";
import { saveAs } from 'file-saver';
import { TranslateInterpolatePipe } from "@alf-nx-workspace/shared/ui";

interface ResultTemplateComponentState {
  variableMap: Map<string, string>,
  workflowResult: string,
  approved: WorkflowApprover[],
  rejectedBy: string,
  rejectedReason: string,
  contentId: number,
  filename: string,
  relatedContent: RelatedContentRepresentation,
  protocol: WorkflowResultProtocol[]
}

@Component({
  selector: 'masterarbeitsanmeldung-result-template',
  template: `
    <ng-container *ngIf="vm$ | async as vm">
      <ng-container *ngIf="vm.workflowResult === workflowResults.successful">
        <a [href]="getContentUrl()"
           (click)="downloadContent($event)">{{ 'LINK_TO_DOCUMENT' | translate }}</a>
      </ng-container>

      <ng-container *ngIf="workflowResult === workflowResults.unsuccessful">

        <ng-container *ngIf="autoBeendetProtocol">
          <div class="margin-top-4">{{ autoBeendetProtocol.result | translate }}</div>
          <div class="mat-caption" [innerHTML]="autoBeendetProtocol.reason | translateInterpolate:variableMap"></div>
        </ng-container>
      </ng-container>
    </ng-container>
  `,
  styles: [``],
  standalone: true,
  imports: [
    TranslateModule,
    AsyncPipe,
    NgIf,
    TranslateInterpolatePipe,
  ]
})
export class ResultTemplateComponent extends BaseResultTemplateComponent {
  public readonly fields = MasterarbeitsanmeldungVariables;
  readonly rxState = inject<RxState<ResultTemplateComponentState>>(RxState);
  readonly vm$ = this.rxState.select(selectSlice(["workflowResult", "protocol", "contentId"]));
  readonly downloadSubject = new Subject<number>();
  readonly activitiContentService = inject(ActivitiContentService);
  private filename;

  ngOnInit() {
    this.connectState();
    this.sideEffects();
  }

  private sideEffects() {
    this.rxState.hold(this.downloadSubject.pipe(
      switchMap((contentId) => fromPromise(this.activitiContentService.contentApi.getRawContent(contentId))),
      tap((content) => saveAs(content, this.rxState.get('filename')))
    ));
  }

  private connectState() {
    this.rxState.connect("contentId", this.variableMapSubject, (oldState, value) => {
      try {
        return parseInt(value.get(this.fields.finaleFreigaben));
      } catch (e) {
        return null;
      }
    });
    this.rxState.connect("relatedContent", this.rxState.select("contentId").pipe(
      filter(contentId => !!contentId),
      switchMap(contentId => fromPromise(this.activitiContentService.contentApi.getContent(contentId)))
    ));
    this.rxState.connect("filename", this.rxState.select("relatedContent").pipe(
      map((value:RelatedContentRepresentation) => {
        return value.name;
      })
    ));
    this.rxState.connect("protocol", this.variableMapSubject, (oldState, value) => {
      try {
        return JSON.parse(this.variableMap.get(this.fields._bokuFlow_workflowResultProtocolJSON));
      } catch (e) {
        return [];
      }
    });
  }

  public downloadContent($event: MouseEvent) {
    $event.preventDefault();

    const contentId = this.rxState.get('contentId');
    fromPromise(this.activitiContentService.contentApi.getContent(contentId))
      .pipe(
        switchMap(({ name }) => of(name)),
        switchMap((name) => {
          this.filename = name;
          return this.activitiContentService.contentApi.getRawContent(contentId);
        })
      )
      .subscribe((content) => saveAs(content, this.filename));
  }

  public getContentUrl(): string {
    return this.activitiContentService.contentApi.getRawContentUrl(this.rxState.get('contentId'));
  }
}
